import { apiClient } from '@/lib/apiClient'
import type { ApiResponse } from '@/types/api.types'
import type { UserProfile, UserProfileUpdate, SavedOperator } from '@/types/user.types'

// ── Profilo ─────────────────────────────────────────────────────────────────

interface PresignedResponse {
  uploadUrl: string
  publicUrl: string
}

/** Recupera il profilo dell'utente loggato (creato on-demand). */
export async function getMyProfile(): Promise<UserProfile> {
  const { data } = await apiClient.get<ApiResponse<UserProfile>>('/me')
  return data.data
}

/** Aggiorna i campi modificabili del profilo. */
export async function updateMyProfile(patch: UserProfileUpdate): Promise<UserProfile> {
  const { data } = await apiClient.patch<ApiResponse<UserProfile>>('/me', patch)
  return data.data
}

/** GDPR delete: rimuove tutti i dati USER dalla piattaforma (non Cognito). */
export async function deleteMyAccount(): Promise<void> {
  await apiClient.delete('/me')
}

/**
 * Upload foto profilo: richiede presigned URL e fa la PUT diretta su S3.
 * Ritorna l'URL pubblico finale da salvare nel profilo via updateMyProfile.
 */
export async function uploadMyAvatar(file: File): Promise<string> {
  const { data } = await apiClient.post<ApiResponse<PresignedResponse>>(
    '/uploads/user-avatar/presigned',
    { contentType: file.type, size: file.size },
  )
  const { uploadUrl, publicUrl } = data.data

  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  })
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`)

  return publicUrl
}

// ── Saved operators ─────────────────────────────────────────────────────────

interface SavedOperatorsResponse {
  items: SavedOperator[]
  total: number
}

export async function listSavedOperators(): Promise<SavedOperator[]> {
  const { data } = await apiClient.get<ApiResponse<SavedOperatorsResponse>>('/me/saved-operators')
  return data.data.items
}

export async function saveOperator(operatorSlug: string): Promise<SavedOperator> {
  const { data } = await apiClient.post<ApiResponse<SavedOperator>>(
    '/me/saved-operators',
    { operatorSlug },
  )
  return data.data
}

export async function unsaveOperator(operatorSlug: string): Promise<void> {
  await apiClient.delete(`/me/saved-operators/${encodeURIComponent(operatorSlug)}`)
}

// ── Operator status (relazione cliente↔operatore) ───────────────────────────

export interface OperatorStatus {
  operatorSlug:          string
  /** userId Cognito dell'operatore (per aprire chat). */
  operatorId:            string
  /** L'utente ha già usato la videocall conoscitiva con questo operatore. */
  hasUsedIntroPair:      boolean
  /** Esiste almeno un appuntamento (intro o paid) fra le due parti. */
  hasAppointmentHistory: boolean
  /** Chat effettivamente disponibile: operatore ha attivato la chat E c'è una
   *  prestazione a pagamento completata fra i due. Sblocca il CTA "Apri chat". */
  chatAvailable:         boolean
}

/** Recupera i flag della relazione cliente↔operatore (solo USER loggato). */
export async function getOperatorStatus(operatorSlug: string): Promise<OperatorStatus> {
  const { data } = await apiClient.get<ApiResponse<OperatorStatus>>(
    `/me/operator-status/${encodeURIComponent(operatorSlug)}`,
  )
  return data.data
}
